import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Download,
  Search,
  RefreshCw,
  Users,
  FileSpreadsheet,
  Phone,
  User,
  ChevronLeft,
  ChevronRight,
  Calendar,
  X,
  Plus,
  FileText,
  Send,
  Mail,
  DollarSign,
  CreditCard,
  Check,
  Clock,
  AlertCircle,
  Edit,
  Ticket,
  PhoneCall,
  CheckCircle,
  Minus,
  Bold,
} from "lucide-react";
import axios from "axios";
import { apiUrl } from "../../constants";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

const EventManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [dateFilter, setDateFilter] = useState("all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [showAddBookingModal, setShowAddBookingModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [generatingInvoice, setGeneratingInvoice] = useState(null);
  const [bookingStatusFilter, setBookingStatusFilter] = useState("all");
  const { isLoggedIn, token } = useSelector((store) => store.userSlice);

  // New booking form state
  const [newBooking, setNewBooking] = useState({
    userName: "",
    phoneNumber: "",
    email: "",
    paymentMode: "cash",
    totalAmount: 0,
    paidAmount: 0,
    tickets: {
      category_5_10: 0,
      category_10_above: 0,
    },
  });

  const ticketPrices = {
    category_5_10: 699, // Price for 5-10 age group
    category_10_above: 999, // Price for 10+ age group
  };

  useEffect(() => {
    let totalPrice = 0;

    // Calculate total based on quantity × price for each category
    totalPrice += newBooking.tickets.category_5_10 * ticketPrices.category_5_10;
    totalPrice +=
      newBooking.tickets.category_10_above * ticketPrices.category_10_above;

    setNewBooking((prev) => ({
      ...prev,
      totalAmount: totalPrice,
    }));
  }, [newBooking.tickets]);

  // Payment update state
  const [paymentUpdate, setPaymentUpdate] = useState({
    additionalAmount: 0,
    paymentMode: "cash",
  });

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showAddBookingModal || showPaymentModal) {
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = "15px";
    } else {
      document.body.style.overflow = "unset";
      document.body.style.paddingRight = "0";
    }
    return () => {
      document.body.style.overflow = "unset";
      document.body.style.paddingRight = "0";
    };
  }, [showAddBookingModal, showPaymentModal]);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.get(`${apiUrl}/event/all`, {
        headers: { token },
      });
      if (response.data.success) {
        setBookings(response.data.data || []);
      } else {
        setError(response.data.message || "Failed to load booking data");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch bookings",
      );
      console.error("Error fetching bookings:", err);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  }, []);

  const getDateRange = useCallback(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (dateFilter) {
      case "today":
        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999);
        return { start: today, end: todayEnd };

      case "yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayEnd = new Date(yesterday);
        yesterdayEnd.setHours(23, 59, 59, 999);
        return { start: yesterday, end: yesterdayEnd };

      case "last7days":
        const last7 = new Date(today);
        last7.setDate(last7.getDate() - 7);
        const last7End = new Date(now);
        last7End.setHours(23, 59, 59, 999);
        return { start: last7, end: last7End };

      case "last30days":
        const last30 = new Date(today);
        last30.setDate(last30.getDate() - 30);
        const last30End = new Date(now);
        last30End.setHours(23, 59, 59, 999);
        return { start: last30, end: last30End };

      case "thisMonth":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        monthStart.setHours(0, 0, 0, 0);
        const monthEnd = new Date(now);
        monthEnd.setHours(23, 59, 59, 999);
        return { start: monthStart, end: monthEnd };

      case "lastMonth":
        const lastMonthStart = new Date(
          now.getFullYear(),
          now.getMonth() - 1,
          1,
        );
        lastMonthStart.setHours(0, 0, 0, 0);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        lastMonthEnd.setHours(23, 59, 59, 999);
        return { start: lastMonthStart, end: lastMonthEnd };

      case "custom":
        if (customStartDate && customEndDate) {
          const start = new Date(customStartDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(customEndDate);
          end.setHours(23, 59, 59, 999);
          return { start, end };
        }
        return null;

      default:
        return null;
    }
  }, [dateFilter, customStartDate, customEndDate]);

  const filterByDate = useCallback(
    (booking) => {
      if (dateFilter === "all") return true;
      const dateRange = getDateRange();
      if (!dateRange) return true;
      const bookingDate = new Date(booking.createdAt);
      if (isNaN(bookingDate.getTime())) return false;
      return (
        bookingDate.getTime() >= dateRange.start.getTime() &&
        bookingDate.getTime() <= dateRange.end.getTime()
      );
    },
    [dateFilter, getDateRange],
  );

  const getPaymentStatus = useCallback((booking) => {
    if (booking.paidAmount >= booking.totalAmount) return "completed";
    if (booking.paidAmount > 0) return "partial";
    return "pending";
  }, []);

  const filteredBookings = useMemo(() => {
    return bookings.filter((booking) => {
      const searchLower = searchTerm.toLowerCase();
      const nameMatch = booking.userName?.toLowerCase().includes(searchLower);
      const phoneMatch = booking.phoneNumber?.includes(searchTerm);
      const emailMatch = booking.email?.toLowerCase().includes(searchLower);
      const dateMatch = filterByDate(booking);

      const paymentStatus = getPaymentStatus(booking);
      const paymentMatch =
        paymentStatusFilter === "all" || paymentStatus === paymentStatusFilter;

      // NEW: Booking status filter logic
      let bookingStatusMatch = true;
      if (bookingStatusFilter === "given") {
        bookingStatusMatch = booking.ticketGiven === true;
      } else if (bookingStatusFilter === "called") {
        bookingStatusMatch = booking.isContacted === true;
      } else if (bookingStatusFilter === "pending") {
        bookingStatusMatch =
          booking.ticketGiven === false || booking.isContacted === false;
      }

      return (
        (nameMatch || phoneMatch || emailMatch) &&
        dateMatch &&
        paymentMatch &&
        bookingStatusMatch
      );
    });
  }, [
    bookings,
    searchTerm,
    filterByDate,
    getPaymentStatus,
    paymentStatusFilter,
    bookingStatusFilter,
  ]);

  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBookings = useMemo(
    () => filteredBookings.slice(startIndex, endIndex),
    [filteredBookings, startIndex, endIndex],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    dateFilter,
    paymentStatusFilter,
    customStartDate,
    customEndDate,
  ]);

  // Increment/Decrement ticket handlers
  const handleTicketChange = (category, delta) => {
    setNewBooking((prev) => ({
      ...prev,
      tickets: {
        ...prev.tickets,
        [category]: Math.max(0, prev.tickets[category] + delta),
      },
    }));
  };

  const handleAddBooking = async (e) => {
    e.preventDefault();

    try {
      const response = await axios.post(`${apiUrl}/event/create`, newBooking, {
        headers: { token },
      });

      if (response.data.success) {
        toast.success("Booking created successfully!");
        setShowAddBookingModal(false);
        setNewBooking({
          userName: "",
          phoneNumber: "",
          email: "",
          paymentMode: "cash",
          totalAmount: 0,
          paidAmount: 0,
          tickets: {
            category_0_5: 0,
            category_5_10: 0,
            category_10_above: 0,
          },
        });
        fetchBookings();
      }
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to create booking. Please try again.",
      );
    }
  };

  const handleUpdatePayment = async (e) => {
    e.preventDefault();

    if (!selectedBooking) return;

    if (paymentUpdate.additionalAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (paymentUpdate.additionalAmount > selectedBooking.remainingAmount) {
      toast.error("Amount cannot exceed remaining amount");
      return;
    }

    try {
      const response = await axios.put(
        `${apiUrl}/event/update-payment/${selectedBooking._id}`,
        paymentUpdate,
        { headers: { token } },
      );

      if (response.data.success) {
        toast.success("Payment updated successfully!");
        setShowPaymentModal(false);
        setSelectedBooking(null);
        setPaymentUpdate({ additionalAmount: 0, paymentMode: "cash" });
        fetchBookings();
      }
    } catch (error) {
      console.error("Error updating payment:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to update payment. Please try again.",
      );
    }
  };

  const toggleTicketGiven = async (bookingId, currentStatus) => {
    try {
      const response = await axios.put(
        `${apiUrl}/event/ticket-given/${bookingId}`,
        { ticketGiven: !currentStatus },
        { headers: { token } },
      );

      if (response.data.success) {
        toast.success(
          `Ticket ${!currentStatus ? "marked as given" : "marked as not given"}`,
        );
        fetchBookings();
      }
    } catch (error) {
      console.error("Error updating ticket status:", error);
      toast.error("Failed to update ticket status");
    }
  };

  const toggleContacted = async (bookingId, currentStatus) => {
    try {
      const response = await axios.put(
        `${apiUrl}/event/contacted/${bookingId}`,
        { isContacted: !currentStatus },
        { headers: { token } },
      );

      if (response.data.success) {
        toast.success(`Contact status updated`);
        fetchBookings();
      }
    } catch (error) {
      console.error("Error updating contact status:", error);
      toast.error("Failed to update contact status");
    }
  };

  const openPaymentModal = (booking) => {
    setSelectedBooking(booking);
    setPaymentUpdate({
      additionalAmount: booking.remainingAmount,
      paymentMode: booking.paymentMode || "cash",
    });
    setShowPaymentModal(true);
  };

  const downloadInvoice = async (bookingId) => {
    setGeneratingInvoice(bookingId);
    try {
      const response = await axios.get(`${apiUrl}/event/invoice/${bookingId}`, {
        responseType: "blob",
        headers: { token },
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Invoice_${bookingId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Invoice downloaded successfully!");
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error("Failed to download invoice. Please try again.");
    } finally {
      setGeneratingInvoice(null);
    }
  };

  const sendInvoice = async (bookingId, phoneNumber) => {
    try {
      const response = await axios.post(
        `${apiUrl}/event/send-invoice`,
        { bookingId, phoneNumber },
        { headers: { token } },
      );

      if (response.data.success) {
        toast.success("Invoice sent successfully!");
      }
    } catch (error) {
      console.error("Error sending invoice:", error);
      toast.error("Failed to send invoice. Please try again.");
    }
  };

  const downloadExcel = async () => {
    setDownloadingExcel(true);

    try {
      const params = new URLSearchParams();

      if (searchTerm) params.append("search", searchTerm);
      if (dateFilter !== "all") {
        params.append("dateFilter", dateFilter);
        if (dateFilter === "custom" && customStartDate && customEndDate) {
          params.append("startDate", customStartDate);
          params.append("endDate", customEndDate);
        }
      }
      if (paymentStatusFilter !== "all") {
        params.append("paymentStatus", paymentStatusFilter);
      }

      const response = await axios.get(
        `${apiUrl}/event/export/excel?${params.toString()}`,
        {
          responseType: "blob",
          headers: { token },
        },
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const filterSuffix =
        dateFilter !== "all" || searchTerm || paymentStatusFilter !== "all"
          ? "_filtered"
          : "";
      link.setAttribute(
        "download",
        `Bookings${filterSuffix}_${new Date().toISOString().split("T")[0]}.xlsx`,
      );

      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(
        `${filteredBookings.length} booking(s) downloaded successfully!`,
      );
    } catch (error) {
      console.error("Error downloading Excel file:", error);
      toast.error("Failed to download Excel file. Please try again.");
    } finally {
      setDownloadingExcel(false);
    }
  };

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    if (value === "custom") {
      setShowCustomDatePicker(true);
    } else {
      setShowCustomDatePicker(false);
      setCustomStartDate("");
      setCustomEndDate("");
    }
  };

  const clearFilters = () => {
    setDateFilter("all");
    setPaymentStatusFilter("all");
    setBookingStatusFilter("all");
    setCustomStartDate("");
    setCustomEndDate("");
    setShowCustomDatePicker(false);
    setSearchTerm("");
  };

  const hasActiveFilters =
    dateFilter !== "all" ||
    paymentStatusFilter !== "all" ||
    bookingStatusFilter !== "all" ||
    searchTerm !== "";

  const getStatusBadge = (booking) => {
    const status = getPaymentStatus(booking);
    const styles = {
      completed: {
        bg: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
        icon: <Check size={14} />,
        text: "Completed",
      },
      partial: {
        bg: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
        icon: <Clock size={14} />,
        text: "Partial",
      },
      pending: {
        bg: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
        icon: <AlertCircle size={14} />,
        text: "Pending",
      },
    };

    const style = styles[status];

    return (
      <span
        style={{
          background: style.bg,
          color: "white",
          padding: "6px 12px",
          borderRadius: "8px",
          fontWeight: "600",
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          fontSize: "12px",
        }}
      >
        {style.icon}
        {style.text}
      </span>
    );
  };

  const totalTickets =
    newBooking.tickets.category_5_10 + newBooking.tickets.category_10_above;

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #1e3a8a 0%, #7c3aed 100%)",
        padding: "40px 20px",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          position: "relative",
        }}
      >
        {/* Loading Overlay */}
        {downloadingExcel && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.7)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
              backdropFilter: "blur(4px)",
            }}
          >
            <div
              style={{
                background: "white",
                borderRadius: "20px",
                padding: "40px 60px",
                boxShadow: "0 25px 50px rgba(0, 0, 0, 0.5)",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  border: "6px solid #f3f4f6",
                  borderTop: "6px solid #10b981",
                  borderRadius: "50%",
                  margin: "0 auto 25px",
                  animation: "spin 1s linear infinite",
                }}
              />
              <h2
                style={{
                  margin: "0 0 10px 0",
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#1a202c",
                }}
              >
                Downloading Excel...
              </h2>
              <p
                style={{
                  margin: 0,
                  color: "#6b7280",
                  fontSize: "16px",
                }}
              >
                Please wait while we prepare your file
              </p>
            </div>
          </div>
        )}

        {/* Payment Update Modal */}
        {showPaymentModal && selectedBooking && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.7)",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              zIndex: 9999,
              backdropFilter: "blur(4px)",
              overflowY: "auto",
              padding: "10px 10px",
            }}
            onClick={() => setShowPaymentModal(false)}
          >
            <div
              style={{
                background: "white",
                borderRadius: "20px",
                padding: "40px",
                maxWidth: "500px",
                width: "90%",
                boxShadow: "0 25px 50px rgba(0, 0, 0, 0.5)",
                margin: "20px",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "30px",
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: "28px",
                    fontWeight: "bold",
                    color: "#1a202c",
                  }}
                >
                  Update Payment
                </h2>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "8px",
                  }}
                >
                  <X size={24} color="#6b7280" />
                </button>
              </div>

              <div
                style={{
                  padding: "20px",
                  background: "#f9fafb",
                  borderRadius: "12px",
                  marginBottom: "25px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                  }}
                >
                  <span style={{ color: "#6b7280", fontSize: "14px" }}>
                    Customer:
                  </span>
                  <span
                    style={{
                      fontWeight: "600",
                      color: "#1f2937",
                      fontSize: "14px",
                    }}
                  >
                    {selectedBooking.userName}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                  }}
                >
                  <span style={{ color: "#6b7280", fontSize: "14px" }}>
                    Total Amount:
                  </span>
                  <span
                    style={{
                      fontWeight: "600",
                      color: "#1f2937",
                      fontSize: "14px",
                    }}
                  >
                    ₹{selectedBooking.totalAmount}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                  }}
                >
                  <span style={{ color: "#6b7280", fontSize: "14px" }}>
                    Already Paid:
                  </span>
                  <span
                    style={{
                      fontWeight: "600",
                      color: "#10b981",
                      fontSize: "14px",
                    }}
                  >
                    ₹{selectedBooking.paidAmount}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    paddingTop: "10px",
                    borderTop: "2px solid #e5e7eb",
                  }}
                >
                  <span
                    style={{
                      color: "#1f2937",
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                  >
                    Remaining:
                  </span>
                  <span
                    style={{
                      fontWeight: "700",
                      color: "#ef4444",
                      fontSize: "18px",
                    }}
                  >
                    ₹{selectedBooking.remainingAmount}
                  </span>
                </div>
              </div>

              <form onSubmit={handleUpdatePayment}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                        marginBottom: "8px",
                      }}
                    >
                      Payment Amount *
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      max={selectedBooking.remainingAmount}
                      value={paymentUpdate.additionalAmount}
                      onChange={(e) =>
                        setPaymentUpdate({
                          ...paymentUpdate,
                          additionalAmount: parseFloat(e.target.value) || 0,
                        })
                      }
                      style={{
                        width: "100%",
                        padding: "12px",
                        border: "2px solid #e5e7eb",
                        borderRadius: "10px",
                        fontSize: "15px",
                        outline: "none",
                        transition: "border-color 0.2s",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#7c3aed")}
                      onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                    />
                    <div
                      style={{
                        marginTop: "8px",
                        display: "flex",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setPaymentUpdate({
                            ...paymentUpdate,
                            additionalAmount: selectedBooking.remainingAmount,
                          })
                        }
                        style={{
                          padding: "6px 12px",
                          background: "#f3f4f6",
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                          fontSize: "12px",
                          cursor: "pointer",
                          color: "#1f2937",
                          fontWeight: "600",
                        }}
                      >
                        Pay Full (₹{selectedBooking.remainingAmount})
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setPaymentUpdate({
                            ...paymentUpdate,
                            additionalAmount:
                              selectedBooking.remainingAmount / 2,
                          })
                        }
                        style={{
                          padding: "6px 12px",
                          background: "#f3f4f6",
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                          fontSize: "12px",
                          cursor: "pointer",
                          color: "#1f2937",
                          fontWeight: "600",
                        }}
                      >
                        Pay Half (₹{selectedBooking.remainingAmount / 2})
                      </button>
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                        marginBottom: "8px",
                      }}
                    >
                      Payment Mode *
                    </label>
                    <select
                      required
                      value={paymentUpdate.paymentMode}
                      onChange={(e) =>
                        setPaymentUpdate({
                          ...paymentUpdate,
                          paymentMode: e.target.value,
                        })
                      }
                      style={{
                        width: "100%",
                        padding: "12px",
                        border: "2px solid #e5e7eb",
                        borderRadius: "10px",
                        fontSize: "15px",
                        outline: "none",
                        height: "auto",
                        cursor: "pointer",
                        transition: "border-color 0.2s",
                      }}
                    >
                      <option value="cash">Cash</option>
                      <option value="online">Online</option>
                    </select>
                  </div>

                  <div
                    style={{
                      padding: "15px",
                      background: "#f0fdf4",
                      borderRadius: "10px",
                      border: "2px solid #10b981",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#059669",
                        marginBottom: "5px",
                      }}
                    >
                      New Remaining Amount
                    </div>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#047857",
                      }}
                    >
                      ₹
                      {selectedBooking.remainingAmount -
                        paymentUpdate.additionalAmount}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "15px",
                      marginTop: "10px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setShowPaymentModal(false)}
                      style={{
                        flex: 1,
                        padding: "14px",
                        background: "white",
                        border: "2px solid #e5e7eb",
                        borderRadius: "12px",
                        fontSize: "16px",
                        fontWeight: "600",
                        cursor: "pointer",
                        color: "#6b7280",
                        transition: "all 0.2s",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{
                        flex: 1,
                        padding: "14px",
                        background:
                          "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                        border: "none",
                        borderRadius: "12px",
                        fontSize: "16px",
                        fontWeight: "600",
                        cursor: "pointer",
                        color: "white",
                        transition: "all 0.2s",
                      }}
                    >
                      Update Payment
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Booking Modal */}
        {showAddBookingModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
              backdropFilter: "blur(4px)",
              overflowY: "auto",
              padding: "20px 0",
            }}
            onClick={() => setShowAddBookingModal(false)}
          >
            <div
              style={{
                background: "white",
                borderRadius: "20px",
                padding: "40px",
                maxWidth: "600px",
                width: "90%",
                maxHeight: "90vh",
                overflow: "auto",
                boxShadow: "0 25px 50px rgba(0, 0, 0, 0.5)",
                margin: "auto",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "30px",
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: "28px",
                    fontWeight: "bold",
                    color: "#1a202c",
                  }}
                >
                  Create New Booking
                </h2>
                <button
                  onClick={() => setShowAddBookingModal(false)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "8px",
                  }}
                >
                  <X size={24} color="#6b7280" />
                </button>
              </div>

              <form onSubmit={handleAddBooking}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                        marginBottom: "8px",
                      }}
                    >
                      Customer Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={newBooking.userName}
                      onChange={(e) =>
                        setNewBooking({
                          ...newBooking,
                          userName: e.target.value,
                        })
                      }
                      style={{
                        width: "100%",
                        padding: "12px",
                        border: "2px solid #e5e7eb",
                        borderRadius: "10px",
                        fontSize: "15px",
                        outline: "none",
                        transition: "border-color 0.2s",
                        boxSizing: "border-box",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#7c3aed")}
                      onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                        marginBottom: "8px",
                      }}
                    >
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      required
                      value={newBooking.phoneNumber}
                      onChange={(e) =>
                        setNewBooking({
                          ...newBooking,
                          phoneNumber: e.target.value,
                        })
                      }
                      style={{
                        width: "100%",
                        padding: "12px",
                        border: "2px solid #e5e7eb",
                        borderRadius: "10px",
                        fontSize: "15px",
                        outline: "none",
                        transition: "border-color 0.2s",
                        boxSizing: "border-box",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#7c3aed")}
                      onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                        marginBottom: "8px",
                      }}
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      value={newBooking.email}
                      onChange={(e) =>
                        setNewBooking({ ...newBooking, email: e.target.value })
                      }
                      style={{
                        width: "100%",
                        padding: "12px",
                        border: "2px solid #e5e7eb",
                        borderRadius: "10px",
                        fontSize: "15px",
                        outline: "none",
                        transition: "border-color 0.2s",
                        boxSizing: "border-box",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#7c3aed")}
                      onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                    />
                  </div>

                  {/* Tickets Section with +/- Buttons */}
                  <div
                    style={{
                      padding: "20px",
                      background: "#f9fafb",
                      borderRadius: "12px",
                      border: "2px solid #e5e7eb",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "15px",
                      }}
                    >
                      <Ticket size={20} color="#7c3aed" />
                      <span
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "#1f2937",
                        }}
                      >
                        Ticket Categories
                      </span>
                    </div>

                    {/* Category 0-5 */}
                    {/* <div style={{ marginBottom: "15px" }}>
                      <label
                        style={{
                          display: "block",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#6b7280",
                          marginBottom: "8px",
                        }}
                      >
                        0-5 Customers
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => handleTicketChange("category_0_5", -1)}
                          disabled={newBooking.tickets.category_0_5 === 0}
                          style={{
                            padding: "10px",
                            background:
                              newBooking.tickets.category_0_5 === 0
                                ? "#f3f4f6"
                                : "#ef4444",
                            border: "none",
                            borderRadius: "8px",
                            cursor:
                              newBooking.tickets.category_0_5 === 0
                                ? "not-allowed"
                                : "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.2s",
                          }}
                        >
                          <Minus size={18} color="white" />
                        </button>
                        <input
                          type="number"
                          min="0"
                          value={newBooking.tickets.category_0_5}
                          onChange={(e) =>
                            setNewBooking({
                              ...newBooking,
                              tickets: {
                                ...newBooking.tickets,
                                category_0_5: Math.max(
                                  0,
                                  parseInt(e.target.value) || 0,
                                ),
                              },
                            })
                          }
                          style={{
                            flex: 1,
                            padding: "12px",
                            border: "2px solid #e5e7eb",
                            borderRadius: "8px",
                            fontSize: "16px",
                            fontWeight: "600",
                            textAlign: "center",
                            outline: "none",
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleTicketChange("category_0_5", 1)}
                          style={{
                            padding: "10px",
                            background: "#10b981",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.2s",
                          }}
                        >
                          <Plus size={18} color="white" />
                        </button>
                      </div>
                    </div> */}

                    {/* Category 5-10 */}
                    <div style={{ marginBottom: "15px" }}>
                      <label
                        style={{
                          display: "block",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#6b7280",
                          marginBottom: "8px",
                        }}
                      >
                        5-10 Customers
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            handleTicketChange("category_5_10", -1)
                          }
                          disabled={newBooking.tickets.category_5_10 === 0}
                          style={{
                            padding: "10px",
                            background:
                              newBooking.tickets.category_5_10 === 0
                                ? "#f3f4f6"
                                : "#ef4444",
                            border: "none",
                            borderRadius: "8px",
                            cursor:
                              newBooking.tickets.category_5_10 === 0
                                ? "not-allowed"
                                : "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.2s",
                          }}
                        >
                          <Minus size={18} color="white" />
                        </button>
                        <input
                          type="number"
                          min="0"
                          value={newBooking.tickets.category_5_10}
                          onChange={(e) =>
                            setNewBooking({
                              ...newBooking,
                              tickets: {
                                ...newBooking.tickets,
                                category_5_10: Math.max(
                                  0,
                                  parseInt(e.target.value) || 0,
                                ),
                              },
                            })
                          }
                          style={{
                            flex: 1,
                            padding: "12px",
                            border: "2px solid #e5e7eb",
                            borderRadius: "8px",
                            fontSize: "16px",
                            fontWeight: "600",
                            textAlign: "center",
                            outline: "none",
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleTicketChange("category_5_10", 1)}
                          style={{
                            padding: "10px",
                            background: "#10b981",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.2s",
                          }}
                        >
                          <Plus size={18} color="white" />
                        </button>
                      </div>
                    </div>

                    {/* Category 10+ */}
                    <div style={{ marginBottom: "10px" }}>
                      <label
                        style={{
                          display: "block",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#6b7280",
                          marginBottom: "8px",
                        }}
                      >
                        10+ Customers
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            handleTicketChange("category_10_above", -1)
                          }
                          disabled={newBooking.tickets.category_10_above === 0}
                          style={{
                            padding: "10px",
                            background:
                              newBooking.tickets.category_10_above === 0
                                ? "#f3f4f6"
                                : "#ef4444",
                            border: "none",
                            borderRadius: "8px",
                            cursor:
                              newBooking.tickets.category_10_above === 0
                                ? "not-allowed"
                                : "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.2s",
                          }}
                        >
                          <Minus size={18} color="white" />
                        </button>
                        <input
                          type="number"
                          min="0"
                          value={newBooking.tickets.category_10_above}
                          onChange={(e) =>
                            setNewBooking({
                              ...newBooking,
                              tickets: {
                                ...newBooking.tickets,
                                category_10_above: Math.max(
                                  0,
                                  parseInt(e.target.value) || 0,
                                ),
                              },
                            })
                          }
                          style={{
                            flex: 1,
                            padding: "12px",
                            border: "2px solid #e5e7eb",
                            borderRadius: "8px",
                            fontSize: "16px",
                            fontWeight: "600",
                            textAlign: "center",
                            outline: "none",
                          }}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            handleTicketChange("category_10_above", 1)
                          }
                          style={{
                            padding: "10px",
                            background: "#10b981",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.2s",
                          }}
                        >
                          <Plus size={18} color="white" />
                        </button>
                      </div>
                    </div>

                    {/* Total Tickets */}
                    <div
                      style={{
                        marginTop: "15px",
                        paddingTop: "15px",
                        borderTop: "2px solid #e5e7eb",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#1f2937",
                        }}
                      >
                        Total Tickets:
                      </span>
                      <span
                        style={{
                          fontSize: "20px",
                          fontWeight: "bold",
                          color: "#7c3aed",
                        }}
                      >
                        {totalTickets}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                        marginBottom: "8px",
                      }}
                    >
                      Payment Mode *
                    </label>
                    <select
                      required
                      value={newBooking.paymentMode}
                      onChange={(e) =>
                        setNewBooking({
                          ...newBooking,
                          paymentMode: e.target.value,
                        })
                      }
                      style={{
                        width: "100%",
                        padding: "12px",
                        border: "2px solid #e5e7eb",
                        borderRadius: "10px",
                        fontSize: "15px",
                        outline: "none",
                        height: "auto",
                        cursor: "pointer",
                      }}
                    >
                      <option value="cash">Cash</option>
                      <option value="online">Online</option>
                    </select>
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                        marginBottom: "8px",
                      }}
                    >
                      Paid Amount *
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      max={newBooking.totalAmount}
                      value={newBooking.paidAmount}
                      onChange={(e) =>
                        setNewBooking({
                          ...newBooking,
                          paidAmount: parseFloat(e.target.value) || 0,
                        })
                      }
                      style={{
                        width: "100%",
                        padding: "12px",
                        border: "2px solid #e5e7eb",
                        borderRadius: "10px",
                        fontSize: "15px",
                        outline: "none",
                        transition: "border-color 0.2s",
                        boxSizing: "border-box",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#7c3aed")}
                      onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                    />
                  </div>

                  <div
                    style={{
                      padding: "15px",
                      background: "#f9fafb",
                      borderRadius: "10px",
                      border: "2px solid #e5e7eb",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#6b7280",
                        marginBottom: "5px",
                      }}
                    >
                      Total Amount:
                      <span
                        style={{
                          fontWeight: "bold",
                          color: "green",
                          marginLeft: "5px",
                        }}
                      >
                        ₹{newBooking.totalAmount}
                      </span>
                    </div>

                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#6b7280",
                        marginBottom: "5px",
                      }}
                    >
                      Remaining Amount
                    </div>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#1f2937",
                      }}
                    >
                      ₹{newBooking.totalAmount - newBooking.paidAmount}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "15px",
                      marginTop: "10px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setShowAddBookingModal(false)}
                      style={{
                        flex: 1,
                        padding: "14px",
                        background: "white",
                        border: "2px solid #e5e7eb",
                        borderRadius: "12px",
                        fontSize: "16px",
                        fontWeight: "600",
                        cursor: "pointer",
                        color: "#6b7280",
                        transition: "all 0.2s",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{
                        flex: 1,
                        padding: "14px",
                        background:
                          "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)",
                        border: "none",
                        borderRadius: "12px",
                        fontSize: "16px",
                        fontWeight: "600",
                        cursor: "pointer",
                        color: "white",
                        transition: "all 0.2s",
                      }}
                    >
                      Create Booking
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Header Card */}
        <div
          style={{
            background: "white",
            borderRadius: "20px",
            padding: "30px",
            marginBottom: "30px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "20px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #1e3a8a 0%, #7c3aed 100%)",
                  padding: "15px",
                  borderRadius: "15px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Users size={32} color="white" />
              </div>
              <div>
                <h1
                  style={{
                    margin: 0,
                    fontSize: "28px",
                    fontWeight: "bold",
                    color: "#1a202c",
                  }}
                >
                  Event Booking Management
                </h1>
                <p
                  style={{
                    margin: "5px 0 0 0",
                    color: "#718096",
                    fontSize: "14px",
                  }}
                >
                  {filteredBookings.length} bookings found
                </p>
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button
                onClick={() => setShowAddBookingModal(true)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "12px 24px",
                  background:
                    "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)",
                  border: "none",
                  color: "white",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  boxShadow: "0 4px 12px rgba(124, 58, 237, 0.3)",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow =
                    "0 6px 20px rgba(124, 58, 237, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow =
                    "0 4px 12px rgba(124, 58, 237, 0.3)";
                }}
              >
                <Plus size={18} />
                Add Booking
              </button>

              <button
                onClick={fetchBookings}
                disabled={loading}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "12px 24px",
                  background: "white",
                  border: "2px solid #7c3aed",
                  color: "#7c3aed",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "all 0.3s ease",
                  opacity: loading ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.background = "#7c3aed";
                    e.target.style.color = "white";
                    e.target.style.transform = "translateY(-2px)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "white";
                  e.target.style.color = "#7c3aed";
                  e.target.style.transform = "translateY(0)";
                }}
              >
                <RefreshCw
                  size={18}
                  style={{
                    animation: loading ? "spin 1s linear infinite" : "none",
                  }}
                />
                Refresh
              </button>

              <button
                onClick={downloadExcel}
                disabled={filteredBookings.length === 0 || downloadingExcel}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "12px 24px",
                  background: downloadingExcel
                    ? "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)"
                    : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  border: "none",
                  color: "white",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor:
                    filteredBookings.length === 0 || downloadingExcel
                      ? "not-allowed"
                      : "pointer",
                  transition: "all 0.3s ease",
                  boxShadow: downloadingExcel
                    ? "0 4px 12px rgba(107, 114, 128, 0.3)"
                    : "0 4px 12px rgba(16, 185, 129, 0.3)",
                  opacity:
                    filteredBookings.length === 0 || downloadingExcel ? 0.7 : 1,
                }}
                onMouseEnter={(e) => {
                  if (filteredBookings.length > 0 && !downloadingExcel) {
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow =
                      "0 6px 20px rgba(16, 185, 129, 0.4)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = downloadingExcel
                    ? "0 4px 12px rgba(107, 114, 128, 0.3)"
                    : "0 4px 12px rgba(16, 185, 129, 0.3)";
                }}
              >
                <Download
                  size={18}
                  style={{
                    animation: downloadingExcel
                      ? "spin 1s linear infinite"
                      : "none",
                  }}
                />
                {downloadingExcel
                  ? "Downloading..."
                  : `Download Excel (${filteredBookings.length})`}
              </button>
            </div>
          </div>

          {/* Filters Section */}
          <div
            style={{
              marginTop: "25px",
              padding: "20px",
              background: "#f9fafb",
              borderRadius: "12px",
              border: "2px solid #e5e7eb",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "15px",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <Calendar size={20} color="#7c3aed" />
                <span
                  style={{
                    fontSize: "15px",
                    fontWeight: "600",
                    color: "#1f2937",
                  }}
                >
                  Filters
                </span>
              </div>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "6px 14px",
                    background: "#ef4444",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#dc2626";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "#ef4444";
                  }}
                >
                  <X size={14} />
                  Clear All Filters
                </button>
              )}
            </div>

            {/* Date Filter */}
            <div style={{ marginBottom: "15px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#6b7280",
                  marginBottom: "8px",
                }}
              >
                Date Range
              </label>
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                {[
                  { value: "all", label: "All Time" },
                  { value: "today", label: "Today" },
                  { value: "yesterday", label: "Yesterday" },
                  { value: "last7days", label: "Last 7 Days" },
                  { value: "last30days", label: "Last 30 Days" },
                  { value: "thisMonth", label: "This Month" },
                  { value: "lastMonth", label: "Last Month" },
                  { value: "custom", label: "Custom Range" },
                ].map((filter) => (
                  <button
                    key={filter.value}
                    onClick={() => handleDateFilterChange(filter.value)}
                    style={{
                      padding: "8px 16px",
                      background:
                        dateFilter === filter.value
                          ? "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)"
                          : "white",
                      color: dateFilter === filter.value ? "white" : "#1f2937",
                      border:
                        dateFilter === filter.value
                          ? "2px solid #7c3aed"
                          : "2px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "13px",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (dateFilter !== filter.value) {
                        e.target.style.borderColor = "#7c3aed";
                        e.target.style.background = "#f3f4f6";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (dateFilter !== filter.value) {
                        e.target.style.borderColor = "#e5e7eb";
                        e.target.style.background = "white";
                      }
                    }}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>

              {showCustomDatePicker && (
                <div
                  style={{
                    marginTop: "15px",
                    padding: "15px",
                    background: "white",
                    borderRadius: "8px",
                    border: "2px solid #7c3aed",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "15px",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ flex: "1", minWidth: "200px" }}>
                      <label
                        style={{
                          display: "block",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#6b7280",
                          marginBottom: "5px",
                        }}
                      >
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "10px",
                          border: "2px solid #e5e7eb",
                          borderRadius: "8px",
                          fontSize: "14px",
                          outline: "none",
                        }}
                      />
                    </div>
                    <div style={{ flex: "1", minWidth: "200px" }}>
                      <label
                        style={{
                          display: "block",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#6b7280",
                          marginBottom: "5px",
                        }}
                      >
                        End Date
                      </label>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "10px",
                          border: "2px solid #e5e7eb",
                          borderRadius: "8px",
                          fontSize: "14px",
                          outline: "none",
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Payment Status Filter */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#6b7280",
                  marginBottom: "8px",
                }}
              >
                Payment Status
              </label>
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                {[
                  { value: "all", label: "All Status" },
                  { value: "completed", label: "Completed" },
                  { value: "partial", label: "Partial Payment" },
                  { value: "pending", label: "Pending" },
                ].map((filter) => (
                  <button
                    key={filter.value}
                    onClick={() => setPaymentStatusFilter(filter.value)}
                    style={{
                      padding: "8px 16px",
                      background:
                        paymentStatusFilter === filter.value
                          ? "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)"
                          : "white",
                      color:
                        paymentStatusFilter === filter.value
                          ? "white"
                          : "#1f2937",
                      border:
                        paymentStatusFilter === filter.value
                          ? "2px solid #7c3aed"
                          : "2px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "13px",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (paymentStatusFilter !== filter.value) {
                        e.target.style.borderColor = "#7c3aed";
                        e.target.style.background = "#f3f4f6";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (paymentStatusFilter !== filter.value) {
                        e.target.style.borderColor = "#e5e7eb";
                        e.target.style.background = "white";
                      }
                    }}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Booking Status Filter - ADD THIS NEW SECTION */}
            <div style={{ marginTop: "15px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#6b7280",
                  marginBottom: "8px",
                }}
              >
                Booking Status
              </label>
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                {[
                  { value: "all", label: "All Bookings" },
                  { value: "given", label: "Ticket Given" },
                  { value: "called", label: "Customer Called" },
                  { value: "pending", label: "Pending Action" },
                ].map((filter) => (
                  <button
                    key={filter.value}
                    onClick={() => setBookingStatusFilter(filter.value)}
                    style={{
                      padding: "8px 16px",
                      background:
                        bookingStatusFilter === filter.value
                          ? "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)"
                          : "white",
                      color:
                        bookingStatusFilter === filter.value
                          ? "white"
                          : "#1f2937",
                      border:
                        bookingStatusFilter === filter.value
                          ? "2px solid #7c3aed"
                          : "2px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "13px",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (bookingStatusFilter !== filter.value) {
                        e.target.style.borderColor = "#7c3aed";
                        e.target.style.background = "#f3f4f6";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (bookingStatusFilter !== filter.value) {
                        e.target.style.borderColor = "#e5e7eb";
                        e.target.style.background = "white";
                      }
                    }}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div
            style={{
              marginTop: "20px",
              position: "relative",
            }}
          >
            <Search
              size={20}
              style={{
                position: "absolute",
                left: "15px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#9ca3af",
              }}
            />
            <input
              type="text"
              placeholder="Search by name, phone, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "14px 14px 14px 45px",
                border: "2px solid #e5e7eb",
                borderRadius: "12px",
                fontSize: "15px",
                outline: "none",
                transition: "all 0.3s ease",
                boxSizing: "border-box",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#7c3aed";
                e.target.style.boxShadow = "0 0 0 3px rgba(124, 58, 237, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#e5e7eb";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              background: "#fee2e2",
              border: "2px solid #ef4444",
              borderRadius: "12px",
              padding: "16px",
              marginBottom: "20px",
              color: "#991b1b",
              fontSize: "14px",
            }}
          >
            ⚠️ {error}
          </div>
        )}

        {/* Table Card */}
        <div
          style={{
            background: "white",
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          }}
        >
          {loading ? (
            <div
              style={{
                padding: "60px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  border: "4px solid #f3f4f6",
                  borderTop: "4px solid #7c3aed",
                  borderRadius: "50%",
                  margin: "0 auto 20px",
                  animation: "spin 1s linear infinite",
                }}
              />
              <p style={{ color: "#6b7280", fontSize: "16px" }}>
                Loading bookings...
              </p>
            </div>
          ) : filteredBookings.length === 0 ? (
            <div
              style={{
                padding: "60px",
                textAlign: "center",
              }}
            >
              <FileSpreadsheet
                size={64}
                color="#d1d5db"
                style={{ margin: "0 auto 20px" }}
              />
              <p style={{ color: "#6b7280", fontSize: "18px", margin: 0 }}>
                No bookings found
              </p>
              <p
                style={{ color: "#9ca3af", fontSize: "14px", marginTop: "8px" }}
              >
                {searchTerm || hasActiveFilters
                  ? "Try adjusting your filters"
                  : "Click 'Add Booking' to create your first booking"}
              </p>
            </div>
          ) : (
            <>
              {/* Items per page selector */}
              <div
                style={{
                  padding: "20px 24px",
                  borderBottom: "1px solid #f3f4f6",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <span
                    style={{
                      color: "#6b7280",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    Show
                  </span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) =>
                      handleItemsPerPageChange(Number(e.target.value))
                    }
                    style={{
                      padding: "8px 12px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                      outline: "none",
                      background: "white",
                    }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span
                    style={{
                      color: "#6b7280",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    entries
                  </span>
                </div>
                <div
                  style={{
                    color: "#6b7280",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Showing {startIndex + 1} to{" "}
                  {Math.min(endIndex, filteredBookings.length)} of{" "}
                  {filteredBookings.length} entries
                </div>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background:
                          "linear-gradient(135deg, #1e3a8a 0%, #7c3aed 100%)",
                        color: "white",
                      }}
                    >
                      <th
                        style={{
                          padding: "18px 24px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "14px",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                        }}
                      >
                        S.No
                      </th>
                      <th
                        style={{
                          padding: "18px 24px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "14px",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <User size={16} />
                          Name
                        </div>
                      </th>
                      <th
                        style={{
                          padding: "18px 24px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "14px",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Phone size={16} />
                          Phone
                        </div>
                      </th>
                      <th
                        style={{
                          padding: "18px 24px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "14px",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Ticket size={16} />
                          Tickets
                        </div>
                      </th>
                      <th
                        style={{
                          padding: "18px 24px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "14px",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <CreditCard size={16} />
                          Payment
                        </div>
                      </th>
                      <th
                        style={{
                          padding: "18px 24px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "14px",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <DollarSign size={16} />
                          Amount
                        </div>
                      </th>
                      <th
                        style={{
                          padding: "18px 24px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "14px",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                        }}
                      >
                        Status
                      </th>
                      <th
                        style={{
                          padding: "18px 24px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "14px",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Calendar size={16} />
                          Date
                        </div>
                      </th>
                      <th
                        style={{
                          padding: "18px 24px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "14px",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentBookings.map((booking, index) => (
                      <tr
                        key={booking._id || startIndex + index}
                        style={{
                          borderBottom: "1px solid #f3f4f6",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f9fafb";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "white";
                        }}
                      >
                        <td
                          style={{
                            padding: "18px 24px",
                            color: "#1f2937",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          {startIndex + index + 1}
                        </td>
                        <td
                          style={{
                            padding: "18px 24px",
                            color: "#1f2937",
                            fontSize: "14px",
                            fontWeight: "600",
                          }}
                        >
                          {booking.userName || "N/A"}
                        </td>
                        <td
                          style={{
                            padding: "18px 24px",
                            color: "#1f2937",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          {booking.phoneNumber || "N/A"}
                        </td>
                        <td
                          style={{
                            padding: "18px 24px",
                            fontSize: "14px",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontSize: "13px",
                                color: "#6b7280",
                                marginBottom: "4px",
                                fontWeight: "500",
                              }}
                            >
                              Kids :{" "}
                              <span
                                style={{ color: "#111827", fontWeight: "600" }}
                              >
                                {booking.tickets?.category_5_10 || 0}
                              </span>
                              {" || "}
                              Adults:{" "}
                              <span
                                style={{ color: "#111827", fontWeight: "600" }}
                              >
                                {booking.tickets?.category_10_above || 0}
                              </span>
                            </div>
                            <div
                              style={{
                                fontWeight: "700",
                                color: "#7c3aed",
                                fontSize: "16px",
                              }}
                            >
                              Total: {booking.totalTickets || 0}
                            </div>
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "18px 24px",
                            fontSize: "14px",
                          }}
                        >
                          <span
                            style={{
                              background:
                                booking.paymentMode === "online"
                                  ? "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                                  : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                              color: "white",
                              padding: "6px 12px",
                              borderRadius: "8px",
                              fontWeight: "600",
                              fontSize: "12px",
                              textTransform: "uppercase",
                            }}
                          >
                            {booking.paymentMode || "cash"}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: "18px 24px",
                            fontSize: "14px",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontWeight: "700",
                                color: "#1f2937",
                                marginBottom: "4px",
                              }}
                            >
                              ₹{booking.totalAmount || 0}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#10b981",
                                fontWeight: "600",
                              }}
                            >
                              Paid: ₹{booking.paidAmount || 0}
                            </div>
                            {booking.remainingAmount > 0 && (
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#ef4444",
                                  fontWeight: "600",
                                }}
                              >
                                Due: ₹{booking.remainingAmount}
                              </div>
                            )}
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "18px 24px",
                          }}
                        >
                          {getStatusBadge(booking)}
                        </td>
                        <td
                          style={{
                            padding: "18px 24px",
                            color: "#6b7280",
                            fontSize: "14px",
                          }}
                        >
                          {formatDate(booking.createdAt)}
                        </td>
                        <td
                          style={{
                            padding: "18px 24px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              flexWrap: "wrap",
                              alignItems: "center",
                            }}
                          >
                            {/* Ticket Given Toggle */}
                            <button
                              onClick={() =>
                                toggleTicketGiven(
                                  booking._id,
                                  booking.ticketGiven,
                                )
                              }
                              title={
                                booking.ticketGiven
                                  ? "Mark as not given"
                                  : "Mark as given"
                              }
                              style={{
                                padding: "8px 12px",
                                background: booking.ticketGiven
                                  ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                                  : "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
                                border: "none",
                                borderRadius: "8px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                color: "white",
                                fontSize: "12px",
                                fontWeight: "600",
                                transition: "all 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.transform = "translateY(-2px)";
                                e.target.style.boxShadow = booking.ticketGiven
                                  ? "0 4px 12px rgba(16, 185, 129, 0.4)"
                                  : "0 4px 12px rgba(107, 114, 128, 0.4)";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = "translateY(0)";
                                e.target.style.boxShadow = "none";
                              }}
                            >
                              <Ticket size={14} />
                              {booking.ticketGiven ? "Given" : "Pending"}
                            </button>

                            {/* Contact Toggle */}
                            <button
                              onClick={() =>
                                toggleContacted(
                                  booking._id,
                                  booking.isContacted,
                                )
                              }
                              title={
                                booking.isContacted
                                  ? "Mark as not contacted"
                                  : "Mark as contacted"
                              }
                              style={{
                                padding: "8px 12px",
                                background: booking.isContacted
                                  ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                                  : "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
                                border: "none",
                                borderRadius: "8px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                color: "white",
                                fontSize: "12px",
                                fontWeight: "600",
                                transition: "all 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.transform = "translateY(-2px)";
                                e.target.style.boxShadow = booking.isContacted
                                  ? "0 4px 12px rgba(16, 185, 129, 0.4)"
                                  : "0 4px 12px rgba(107, 114, 128, 0.4)";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = "translateY(0)";
                                e.target.style.boxShadow = "none";
                              }}
                            >
                              <PhoneCall size={14} />
                              {booking.isContacted ? "Called" : "Call"}
                            </button>

                            {booking.remainingAmount > 0 && (
                              <button
                                onClick={() => openPaymentModal(booking)}
                                style={{
                                  padding: "8px 12px",
                                  background:
                                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                                  border: "none",
                                  borderRadius: "8px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  color: "white",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  transition: "all 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = "translateY(-2px)";
                                  e.target.style.boxShadow =
                                    "0 4px 12px rgba(245, 158, 11, 0.4)";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = "translateY(0)";
                                  e.target.style.boxShadow = "none";
                                }}
                              >
                                <Edit size={14} />
                                Payment
                              </button>
                            )}
                            <button
                              onClick={() => downloadInvoice(booking._id)}
                              disabled={generatingInvoice === booking._id}
                              style={{
                                padding: "8px 12px",
                                background:
                                  generatingInvoice === booking._id
                                    ? "#d1d5db"
                                    : "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                border: "none",
                                borderRadius: "8px",
                                cursor:
                                  generatingInvoice === booking._id
                                    ? "not-allowed"
                                    : "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                color: "white",
                                fontSize: "12px",
                                fontWeight: "600",
                                transition: "all 0.2s",
                                opacity:
                                  generatingInvoice === booking._id ? 0.6 : 1,
                              }}
                              onMouseEnter={(e) => {
                                if (generatingInvoice !== booking._id) {
                                  e.target.style.transform = "translateY(-2px)";
                                  e.target.style.boxShadow =
                                    "0 4px 12px rgba(59, 130, 246, 0.4)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = "translateY(0)";
                                e.target.style.boxShadow = "none";
                              }}
                            >
                              <FileText size={14} />
                              Invoice
                            </button>
                            {/* <button
                              onClick={() =>
                                sendInvoice(booking._id, booking.phoneNumber)
                              }
                              style={{
                                padding: "8px 12px",
                                background:
                                  "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                border: "none",
                                borderRadius: "8px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                color: "white",
                                fontSize: "12px",
                                fontWeight: "600",
                                transition: "all 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.transform = "translateY(-2px)";
                                e.target.style.boxShadow =
                                  "0 4px 12px rgba(16, 185, 129, 0.4)";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = "translateY(0)";
                                e.target.style.boxShadow = "none";
                              }}
                            >
                              <Send size={14} />
                              Send
                            </button> */}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div
                style={{
                  padding: "20px 24px",
                  borderTop: "1px solid #f3f4f6",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "15px",
                }}
              >
                <div
                  style={{
                    color: "#6b7280",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Page {currentPage} of {totalPages}
                </div>

                <div
                  style={{ display: "flex", gap: "8px", alignItems: "center" }}
                >
                  <button
                    onClick={() => goToPage(1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: "8px 12px",
                      background: currentPage === 1 ? "#f3f4f6" : "white",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: currentPage === 1 ? "#9ca3af" : "#1f2937",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.background = "#7c3aed";
                        e.target.style.color = "white";
                        e.target.style.borderColor = "#7c3aed";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.background = "white";
                        e.target.style.color = "#1f2937";
                        e.target.style.borderColor = "#e5e7eb";
                      }
                    }}
                  >
                    First
                  </button>

                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: "8px 12px",
                      background: currentPage === 1 ? "#f3f4f6" : "white",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.background = "#7c3aed";
                        e.target.style.borderColor = "#7c3aed";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.background = "white";
                        e.target.style.borderColor = "#e5e7eb";
                      }
                    }}
                  >
                    <ChevronLeft
                      size={18}
                      color={currentPage === 1 ? "#9ca3af" : "#1f2937"}
                    />
                  </button>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        style={{
                          padding: "8px 14px",
                          background:
                            currentPage === pageNum
                              ? "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)"
                              : "white",
                          border:
                            currentPage === pageNum
                              ? "2px solid #7c3aed"
                              : "2px solid #e5e7eb",
                          color: currentPage === pageNum ? "white" : "#1f2937",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontSize: "14px",
                          fontWeight: "600",
                          minWidth: "40px",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (currentPage !== pageNum) {
                            e.target.style.background = "#f3f4f6";
                            e.target.style.borderColor = "#7c3aed";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (currentPage !== pageNum) {
                            e.target.style.background = "white";
                            e.target.style.borderColor = "#e5e7eb";
                          }
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: "8px 12px",
                      background:
                        currentPage === totalPages ? "#f3f4f6" : "white",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      cursor:
                        currentPage === totalPages ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.background = "#7c3aed";
                        e.target.style.borderColor = "#7c3aed";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.background = "white";
                        e.target.style.borderColor = "#e5e7eb";
                      }
                    }}
                  >
                    <ChevronRight
                      size={18}
                      color={currentPage === totalPages ? "#9ca3af" : "#1f2937"}
                    />
                  </button>

                  <button
                    onClick={() => goToPage(totalPages)}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: "8px 12px",
                      background:
                        currentPage === totalPages ? "#f3f4f6" : "white",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      cursor:
                        currentPage === totalPages ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: currentPage === totalPages ? "#9ca3af" : "#1f2937",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.background = "#7c3aed";
                        e.target.style.color = "white";
                        e.target.style.borderColor = "#7c3aed";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.background = "white";
                        e.target.style.color = "#1f2937";
                        e.target.style.borderColor = "#e5e7eb";
                      }
                    }}
                  >
                    Last
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default EventManagement;
